/*
 * @Author: Teemo Ding
 * @Date: 2021-09-30 14:00:06
 * @LastEditTime: 2022-06-10 09:37:59
 * @LastEditors: Json Chen
 * @FilePath: /unifyxp-v2-store-portal/src/constrains/TaxRules/index.tsx
 */

import Configuration from '@/src/components/Configuration/Configuration';
import DroppableTable from '@/src/components/DroppableTable/DroppableTable';
import SelectObjectAsValue from '@/src/components/SelectObjectAsValue';
import { useStoreCountries } from '@/src/pages/Settings/Components/SelectCountryArea';
import { searchStoreTaxes } from '@/src/request/settings/tax';
import { TaxApplyType } from '@/src/types/common/tax';
import { SearchOutlined } from '@ant-design/icons';
import { Form, Spin } from 'antd';
import { get } from 'lodash';
import React, { Fragment, useEffect, useState } from 'react';
import Tooltip, {
  NoWrapContainer,
} from 'src/components/GlobalTooltip/GlobalTooltip';
import { Type } from 'src/constrains/ProductsSelection/ProductSelectionWithHsCode';
import { useI18n } from 'src/hooks/useRouterChange';
import { calculateCountryAreasText, formatNumber } from 'src/tools/helper';
import { i18nGet, replaceI18nVariables } from 'src/tools/tools';
import styles from './style.module.less';

interface IProps {
  applyTypes: TaxApplyType[];
  productUuid?: string;
  hideConfig?: boolean;
  disabled?: boolean;
}

interface FormProps {
  value?: any[];
  onChange?: (data) => void;
  id?: string;
}
const RenderSelectTable = ({
  value,
  onChange,
  applyTypes,
  productUuid,
  id,
  disabled,
}: IProps & FormProps) => {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  const { list: country = [] } = useStoreCountries();
  const [taxList, setTaxList] = useState([]);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    searchStoreTaxes(0, 999, {
      countryCodes: [],
      applyTypes,
      productUuid,
      // applyTypes: [TaxApplyType.Order],
    }).then((data) => {
      setTaxList(data.results);
      setLoading(false);
    });
  }, []);

  const applyToList = (productCount) => {
    return {
      [Type.Shipping]: 'Shipping Rate',
      [Type.All]: 'All Products',
      [Type.Order]: 'Order',
      [Type.HSCode]: productCount + ' Products',
      [Type.Specific]: productCount + ' Products',
    };
  };

  const columns = [
    {
      title: getI18n('general.taxRuleTitle'),
      dataIndex: 'ruleTitle',
      key: 'ruleTitle',
      default: true,
      optional: false,
      className: styles.columnClass,
      minWidth: 160,
    },
    {
      title: getI18n('general.taxAppliesto'),
      dataIndex: 'products',
      default: true,
      optional: true,
      key: 'products',
      className: styles.columnClass,
      minWidth: 128,
      childGenerate: (tags, record) =>
        applyToList(record.productCount)[record.applyTo],
    },
    {
      title:
        get(applyTypes, '[0]', '') == Type.Shipping
          ? getI18n('general.taxRuleBasedOnShippingLocation')
          : getI18n('general.taxCountryArea'),
      dataIndex: 'countryAreas',
      default: true,
      optional: true,
      key: 'countryAreas',
      className: styles.columnClass,
      minWidth: 256,
      childGenerate: (countryAreas, record) => {
        return (
          <NoWrapContainer>
            <Tooltip
              overlayClassName={styles.overlayClassName}
              title={countryAreas
                .map(
                  (item) =>
                    `${item?.country?.name} ${calculateCountryAreasText(
                      item,
                      country,
                    )}`,
                )
                .join(',')}>
              {countryAreas
                .map(
                  (item) =>
                    `${item?.country?.name} ${calculateCountryAreasText(
                      item,
                      country,
                    )}`,
                )
                .join(',')}
            </Tooltip>
          </NoWrapContainer>
        );
      },
    },
    {
      title: getI18n('general.condition'),
      dataIndex: 'condition',
      default: true,
      optional: true,
      key: 'condition',
      className: styles.columnClass,
      minWidth: 193,
      childGenerate: (tags, record) => {
        if (!record.conditionsApplied) {
          return 'N/A';
        } else if (record.conditions.maximum === null) {
          return `${formatNumber(record.conditions.minimum)}${
            record.rateCurrency || record.conditions.chargeCurrency
          } ${getI18n('general.andup')}`;
        } else {
          return `${formatNumber(record.conditions.minimum)}${
            record.rateCurrency || record.conditions.chargeCurrency
          } - ${formatNumber(record.conditions.maximum)}${
            record.rateCurrency || record.conditions.chargeCurrency
          }`;
        }
      },
    },
    {
      title: getI18n('general.compound'),
      dataIndex: 'compound',
      default: true,
      optional: true,
      key: 'compound',
      className: styles.columnClass,
      minWidth: 128,
      childGenerate: (text, record) =>
        text ? getI18n('general.yes') : getI18n('general.no'),
    },
    {
      title: getI18n('general.taxRules'),
      dataIndex: 'taxRate',
      default: true,
      optional: true,
      key: 'taxRate',
      className: styles.columnClass,
      minWidth: 192,
      childGenerate: (tags, record) =>
        `${
          record.rateCurrency ? formatNumber(record.taxRate) : record.taxRate
        } ${record.rateCurrency || '%'}`,
    },
  ];

  const handleSelect = (value) => {
    onChange(value);
  };

  return (
    <Spin spinning={loading}>
      <SelectObjectAsValue
        mode='multiple'
        showSearch
        showArrow
        objectAsValue
        options={taxList}
        labelKey='ruleTitle'
        optionFilterProp='label'
        valueKey='uuid'
        size='large'
        value={value}
        onChange={handleSelect}
        suffixIcon={<SearchOutlined className={styles.selectIcon} />}
        style={{
          width: '528px',
        }}
        disabled={disabled}
      />

      <div className={styles.taxRules} id={id}>
        <p className={styles.taxRulesTitle}>
          {getI18n('general.taxRulesTitleContent')}
        </p>
        <DroppableTable
          dataSource={value || []}
          id='taxRules'
          rowKey='uuid'
          onTableChange={onChange}
          pagination={false}
          columns={columns}
          hideAllDragHandlers={disabled}
        />
      </div>
    </Spin>
  );
};

const TaxRules = (props: IProps) => {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  const { applyTypes, productUuid, hideConfig, disabled } = props;
  return (
    <Fragment>
      <Form.Item
        name='taxRules'
        className={styles.taxRulesSelect}
        label={getI18n('general.taxRules')}
        rules={[
          {
            type: 'array',
            required: true,
            message: replaceI18nVariables(
              getI18n('general.fieldisrequired'),
              getI18n('general.taxRules'),
            ),
          },
        ]}
        labelCol={{ span: 24 }}>
        <RenderSelectTable
          applyTypes={applyTypes}
          productUuid={productUuid}
          disabled={disabled}
        />
      </Form.Item>
      {!hideConfig && (
        <div className={styles.configuration}>
          <Configuration
            linkTo='/settings/tax/list'
            configStart={getI18n('general.settaxrulesatStoresettings')}
            configLink={getI18n('general.taxes')}
            configEnd={getI18n('general.section')}
          />
        </div>
      )}
    </Fragment>
  );
};

export default TaxRules;
export { RenderSelectTable };
